import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { FaEnvelope, FaPhone, FaClock, FaUser, FaPaperPlane, FaComments, FaCheckCircle } from 'react-icons/fa';
import emailjs from '@emailjs/browser';

const PageContainer = styled.div`
  width: 100%;
  height: 100%;
  min-height: calc(100vh - 120px);
  padding: 20px;
  background: linear-gradient(180deg, #f9fbff 0%, #f5f8fd 100%);
  display: flex;
  flex-direction: column;
  align-items: center;
  overflow: hidden;
  position: relative;
  
  @media (max-width: 768px) {
    padding: 10px;
    min-height: calc(100vh - 100px);
  }
`;

const ContentCard = styled.div`
  width: 100%;
  max-width: 700px;
  height: calc(100% - 20px);
  max-height: calc(100vh - 120px);
  background: linear-gradient(145deg, #f9fbff 0%, #ffffff 100%);
  border-radius: 16px;
  box-shadow: 0 12px 30px rgba(39, 68, 131, 0.105);
  overflow: hidden;
  display: flex;
  flex-direction: column;
  
  @media (max-width: 768px) {
    max-height: calc(100vh - 100px);
  }
`;

const Header = styled.div`
  background: linear-gradient(110deg, #2d5bed, #5b61ea);
  padding: 28px;
  text-align: center;
  flex-shrink: 0;
  position: relative;
  overflow: hidden;
  
  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: radial-gradient(circle at top right, rgba(255, 255, 255, 0.12) 0%, transparent 60%);
    pointer-events: none;
  }
  
  @media (max-width: 576px) {
    padding: 20px 15px;
  }
`;

const Title = styled.h1`
  color: #ffffff;
  margin: 0;
  font-size: 32px;
  font-weight: 700;
  letter-spacing: 0.5px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  
  svg {
    font-size: 28px;
  }
  
  @media (max-width: 576px) {
    font-size: 26px;
    
    svg {
      font-size: 22px;
    }
  }
`;

const Subtitle = styled.p`
  color: rgba(255, 255, 255, 0.92);
  font-size: 16px;
  margin-top: 12px;
  font-weight: 400;
  max-width: 500px;
  margin-left: auto;
  margin-right: auto;
  line-height: 1.5;
  
  @media (max-width: 576px) {
    font-size: 14px;
    margin-top: 8px;
  }
`;

const FormContainer = styled.div`
  padding: 28px;
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  -ms-overflow-style: none;
  scrollbar-width: none;
  height: 100%;
  display: flex;
  flex-direction: column;
  
  &::-webkit-scrollbar {
    display: none;
  }
  
  @media (max-width: 576px) {
    padding: 20px 15px;
  }
`;

const ScrollableForm = styled.div`
  flex: 1;
  overflow-y: auto;
  padding-right: 5px;
  -ms-overflow-style: none;
  scrollbar-width: none;
  
  &::-webkit-scrollbar {
    display: none;
  }
`;

const FormGroup = styled.div`
  margin-bottom: 18px;
  
  @media (max-width: 576px) {
    margin-bottom: 14px;
  }
`;

const Label = styled.label`
  display: flex;
  align-items: center;
  color: #33445e;
  margin-bottom: 8px;
  font-weight: 500;
  gap: 8px;
  
  svg {
    color: #335cf4;
    font-size: 16px;
  }
  
  .required {
    color: #d63c3c;
    margin-left: 4px;
  }
  
  @media (max-width: 576px) {
    margin-bottom: 5px;
    font-size: 14px;
  }
`;

const Input = styled.input`
  width: 100%;
  padding: 12px 14px;
  border-radius: 8px;
  border: 1px solid #e0e8f5;
  background: linear-gradient(135deg, #ffffff 0%, #f8faff 100%);
  color: #192947;
  font-size: 15px;
  transition: all 0.3s;
  
  &:focus {
    outline: none;
    border-color: #335cf4;
    box-shadow: 0 0 0 2px rgba(51, 92, 244, 0.25);
  }
  
  @media (max-width: 576px) {
    padding: 10px 12px;
    font-size: 14px;
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 12px 14px;
  border-radius: 8px;
  border: 1px solid #e0e8f5;
  background: linear-gradient(135deg, #ffffff 0%, #f8faff 100%);
  color: #192947;
  font-size: 15px;
  min-height: 110px;
  resize: vertical;
  transition: all 0.3s;
  
  &:focus {
    outline: none;
    border-color: #335cf4;
    box-shadow: 0 0 0 2px rgba(51, 92, 244, 0.25);
  }
  
  @media (max-width: 576px) {
    padding: 10px 12px;
    font-size: 14px;
    min-height: 90px;
  }
`;

const Button = styled.button`
  background: linear-gradient(135deg, #2d5bed, #335cf4);
  color: white;
  border: none;
  border-radius: 8px;
  padding: 13px 24px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s;
  width: 100%;
  margin-top: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  letter-spacing: 0.5px;
  
  svg {
    font-size: 18px;
  }
  
  &:hover {
    background: linear-gradient(135deg, #2d5bed, #2d5bed);
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(45, 91, 237, 0.4);
  }
  
  &:active {
    transform: translateY(0);
    box-shadow: 0 2px 6px rgba(45, 91, 237, 0.4);
  }
  
  &:disabled {
    background: linear-gradient(135deg, #4b5563, #e6ecf6);
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
  
  @media (max-width: 576px) {
    padding: 11px 20px;
    font-size: 15px;
  }
`;

const ContactInfo = styled.div`
  margin-top: 24px;
  border-top: 1px solid #edf1f9;
  padding-top: 22px;
  display: flex;
  justify-content: space-around;
  flex-wrap: wrap;
  
  @media (max-width: 576px) {
    flex-direction: column;
    gap: 18px;
    padding-top: 16px;
    margin-top: 18px;
  }
`;

const InfoItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  color: #33445e;
  width: 30%;
  
  @media (max-width: 576px) {
    width: 100%;
    flex-direction: row;
    text-align: left;
    gap: 15px;
  }
`;

const IconContainer = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: linear-gradient(135deg, rgba(45, 91, 237, 0.1), rgba(51, 92, 244, 0.1));
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 12px;
  border: 1px solid rgba(51, 92, 244, 0.2);
  
  svg {
    color: #335cf4;
    font-size: 20px;
  }
  
  @media (max-width: 576px) {
    margin-bottom: 0;
    width: 42px;
    height: 42px;
  }
`;

const InfoContent = styled.div`
  display: flex;
  flex-direction: column;
  
  @media (max-width: 576px) {
    flex: 1;
  }
`;

const InfoTitle = styled.h3`
  margin: 0 0 8px 0;
  color: #335cf4;
  font-size: 17px;
  font-weight: 600;
  
  @media (max-width: 576px) {
    margin: 0 0 4px 0;
    font-size: 16px;
  }
`;

const InfoText = styled.p`
  margin: 0;
  font-size: 15px;
  color: #33445e;
  
  @media (max-width: 576px) {
    font-size: 14px;
  }
`;

const SuccessMessage = styled.div`
  background-color: rgba(18, 165, 113, 0.15);
  border: 1px solid rgba(18, 165, 113, 0.3);
  border-radius: 8px;
  padding: 16px;
  color: #0f8f63;
  margin-bottom: 24px;
  text-align: center;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  font-weight: 500;
  
  svg {
    font-size: 20px;
  }
  
  @media (max-width: 576px) {
    padding: 14px;
    margin-bottom: 20px;
    font-size: 14px;
    
    svg {
      font-size: 18px;
    }
  }
`;

const ErrorMessage = styled.div`
  background-color: rgba(229, 72, 77, 0.15);
  border: 1px solid rgba(229, 72, 77, 0.3);
  border-radius: 8px;
  padding: 16px;
  color: #d63c3c;
  margin-bottom: 24px;
  text-align: center;
  font-weight: 500;
  
  @media (max-width: 576px) {
    padding: 14px;
    margin-bottom: 20px;
    font-size: 14px;
  }
`;

const ContactUs = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);
  
  // Initialize EmailJS
  useEffect(() => {
    emailjs.init({
      publicKey: "p9w9tLn-63_zYsHJV", // Use your EmailJS public key
    });
  }, []);
  
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);
    
    try {
      // Define the template parameters for EmailJS
      const templateParams = {
        name: formData.name,
        email: formData.email,
        subject: formData.subject,
        message: formData.message
      };
      
      // Send the email using EmailJS
      const response = await emailjs.send(
        'service_796lk8q', // Your EmailJS service ID
        'template_29ksomt', // Your EmailJS template ID
        templateParams
      );
      
      console.log('Email sent successfully!', response);
      
      setSubmitted(true);
      setFormData({
        name: '',
        email: '',
        subject: '',
        message: ''
      });
    } catch (error) {
      console.error('Failed to send email:', error);
      setError('Failed to send message. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };
  
  return (
    <PageContainer>
      <ContentCard>
        <Header>
          <Title><FaComments /> Contact us</Title>
          <Subtitle>We'd love to hear from you. Send us a message and we'll respond as soon as possible.</Subtitle>
        </Header>
        
        <FormContainer>
          {submitted && (
            <SuccessMessage>
              <FaCheckCircle />
              Thank you for your message! We'll get back to you soon.
            </SuccessMessage>
          )}
          
          {error && (
            <ErrorMessage>
              {error}
            </ErrorMessage>
          )}
          
          <ScrollableForm>
            <form onSubmit={handleSubmit}>
              <FormGroup>
                <Label htmlFor="name">
                  <FaUser />Your Name<span className="required">*</span>
                </Label>
                <Input 
                  type="text" 
                  id="name" 
                  name="name" 
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Enter your name"
                  required
                />
              </FormGroup>
              
              <FormGroup>
                <Label htmlFor="email">
                  <FaEnvelope />Email Address<span className="required">*</span>
                </Label>
                <Input 
                  type="email" 
                  id="email" 
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="Enter your email address"
                  required
                />
              </FormGroup>
              
              <FormGroup>
                <Label htmlFor="subject">
                  <FaPaperPlane />Subject<span className="required">*</span>
                </Label>
                <Input 
                  type="text" 
                  id="subject" 
                  name="subject"
                  value={formData.subject}
                  onChange={handleChange}
                  placeholder="What is this regarding?"
                  required
                />
              </FormGroup>
              
              <FormGroup>
                <Label htmlFor="message">
                  <FaComments />Message<span className="required">*</span>
                </Label>
                <TextArea 
                  id="message" 
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  placeholder="Type your message here..."
                  required
                />
              </FormGroup>
              
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting ? 'Sending...' : 'Send Message'} <FaPaperPlane />
              </Button>
            </form>
            
            <ContactInfo>
              <InfoItem>
                <IconContainer>
                  <FaEnvelope />
                </IconContainer>
                <InfoContent>
                  <InfoTitle>Email</InfoTitle>
                  <InfoText>support@imavenai.com</InfoText>
                </InfoContent>
              </InfoItem>
              
              <InfoItem>
                <IconContainer>
                  <FaPhone />
                </IconContainer>
                <InfoContent>
                  <InfoTitle>Phone</InfoTitle>
                  <InfoText>Available soon</InfoText>
                </InfoContent>
              </InfoItem>
              
              <InfoItem>
                <IconContainer>
                  <FaClock />
                </IconContainer>
                <InfoContent>
                  <InfoTitle>Hours</InfoTitle>
                  <InfoText>7 days a week: 10AM - 10PM (GMT+3)</InfoText>
                </InfoContent>
              </InfoItem>
            </ContactInfo>
          </ScrollableForm>
        </FormContainer>
      </ContentCard>
    </PageContainer>
  );
};

export default ContactUs; 